import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BookOpen,
  ShoppingCart,
  Users,
  FileSignature,
  Receipt,
  Wallet,
  CalendarRange,
  Library,
  ListChecks,
} from 'lucide-react';
import { Heading } from '../../layout';
import { PillTabBar } from '../../reusable';
import { useUIColors } from '../../../hooks/useTheme';
import {
  PERIOD_OPTIONS,
  MONTH_OPTIONS,
  getAvailableYearsFromDates,
  resolvePeriodSelection,
  resolveChartAxisColumns,
  periodLabelStyle,
  periodSelectStyle,
} from '../../placeholders/periodSummaryUtils';
import { getRegisterDocuments } from '../../../services/treasuryAPI';
import AccountingRegisterPanel from './accounting/AccountingRegisterPanel';
import AccountingRendicionesPanel from './accounting/AccountingRendicionesPanel';
import AccountingJournalPanel from './accounting/AccountingJournalPanel';
import AccountingLedgerPanel from './accounting/AccountingLedgerPanel';
import AccountingMonthlyChart from './accounting/AccountingMonthlyChart';
import AccountingPeriodSummary from './accounting/AccountingPeriodSummary';
import AccountingSalesOverview from './accounting/AccountingSalesOverview';

/** Libros de registro que comparten un solo GET /registers (no rendiciones). */
const SHARED_REGISTER_SECTIONS = new Set([
  'ventas',
  'compras',
  'remuneraciones',
  'honorarios',
  'invoice',
]);

const NAV_ITEMS = [
  { key: 'ventas', label: 'Libro de ventas', icon: BookOpen },
  { key: 'compras', label: 'Libro de compras', icon: ShoppingCart },
  { key: 'remuneraciones', label: 'Remuneraciones', icon: Users },
  { key: 'honorarios', label: 'Honorarios', icon: FileSignature },
  { key: 'invoice', label: 'Invoice', icon: Receipt },
  { key: 'rendiciones', label: 'Rendiciones', icon: Wallet },
  { key: 'diario', label: 'Libro diario', icon: CalendarRange },
  { key: 'mayor', label: 'Libro mayor', icon: Library },
  { key: 'asientos', label: 'Asientos', icon: ListChecks },
];

const REGISTER_META = {
  ventas: {
    title: 'Libro de ventas',
    subtitle: 'Documentos de venta emitidos (facturas, boletas, notas).',
  },
  compras: {
    title: 'Libro de compras',
    subtitle: 'Documentos de compra recibidos de proveedores.',
  },
  remuneraciones: {
    title: 'Libro de remuneraciones',
    subtitle: 'Liquidaciones y movimientos de personal.',
  },
  honorarios: {
    title: 'Libro de honorarios',
    subtitle: 'Boletas de honorarios y prestadores de servicios.',
  },
  invoice: {
    title: 'Invoice',
    subtitle: 'Registro operativo de facturación electrónica / invoices.',
  },
  rendiciones: {
    title: 'Rendiciones',
    subtitle: 'Fondos por rendir, viáticos y rendiciones de gastos.',
  },
};

const SECTION_PERIOD_TITLES = {
  ventas: { monthly: 'Resumen libro de ventas', annual: 'Resumen libro de ventas anual' },
  compras: { monthly: 'Resumen libro de compras', annual: 'Resumen libro de compras anual' },
  remuneraciones: { monthly: 'Resumen remuneraciones', annual: 'Resumen remuneraciones anual' },
  honorarios: { monthly: 'Resumen honorarios', annual: 'Resumen honorarios anual' },
  invoice: { monthly: 'Resumen invoice', annual: 'Resumen invoice anual' },
  rendiciones: { monthly: 'Resumen rendiciones', annual: 'Resumen rendiciones anual' },
  diario: { monthly: 'Resumen libro diario', annual: 'Resumen libro diario anual' },
  asientos: { monthly: 'Resumen asientos', annual: 'Resumen asientos anual' },
  mayor: { monthly: 'Resumen libro mayor', annual: 'Resumen libro mayor anual' },
};

const formatApiDate = (date) => {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const FinanceAccountingWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const [section, setSection] = useState('ventas');
  const [booksRevision, setBooksRevision] = useState(0);
  const [mayorFocusedAccountIds, setMayorFocusedAccountIds] = useState([]);
  const [periodFilter, setPeriodFilter] = useState('last_36');
  const [secondaryYear, setSecondaryYear] = useState(String(new Date().getFullYear()));
  const [secondaryMonth, setSecondaryMonth] = useState(String(new Date().getMonth()));
  const [dateValues, setDateValues] = useState([]);
  const [sharedRegisterDocs, setSharedRegisterDocs] = useState([]);
  const [sharedRegisterLoading, setSharedRegisterLoading] = useState(true);
  const [sharedRegisterError, setSharedRegisterError] = useState('');

  const notifyBooksChanged = () => setBooksRevision((value) => value + 1);

  const usesSharedRegisterDocs = SHARED_REGISTER_SECTIONS.has(section);

  useEffect(() => {
    if (!usesSharedRegisterDocs) {
      setSharedRegisterDocs([]);
      setSharedRegisterLoading(false);
      setSharedRegisterError('');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setSharedRegisterLoading(true);
        setSharedRegisterError('');
        const payload = await getRegisterDocuments({ register_type: section });
        if (cancelled) return;
        setSharedRegisterDocs(Array.isArray(payload) ? payload : []);
      } catch (err) {
        if (cancelled) return;
        setSharedRegisterError(err.message || 'No se pudo cargar el libro.');
        setSharedRegisterDocs([]);
      } finally {
        if (!cancelled) setSharedRegisterLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [section, booksRevision, usesSharedRegisterDocs]);

  const handleMayorAccountFocus = useCallback((ids) => {
    const next = Array.isArray(ids) ? ids.map(Number).filter(Boolean) : [];
    setMayorFocusedAccountIds((prev) => {
      if (prev.length === next.length && prev.every((id, index) => id === next[index])) {
        return prev;
      }
      return next;
    });
  }, []);

  const handleDateValuesChange = useCallback((dates) => {
    setDateValues((prev) => {
      const next = Array.isArray(dates) ? dates : [];
      if (
        prev.length === next.length &&
        prev.every((date, index) => date?.getTime?.() === next[index]?.getTime?.())
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const availableYears = useMemo(() => getAvailableYearsFromDates(dateValues), [dateValues]);

  const titles = SECTION_PERIOD_TITLES[section] || {
    monthly: 'Resumen contable',
    annual: 'Resumen contable anual',
  };

  const selectedPeriod = useMemo(
    () =>
      resolvePeriodSelection({
        period: periodFilter,
        dateValues,
        secondaryYear,
        secondaryMonth,
        titles,
      }),
    [periodFilter, dateValues, secondaryYear, secondaryMonth, titles]
  );

  const chartColumns = useMemo(() => resolveChartAxisColumns(selectedPeriod), [selectedPeriod]);

  useEffect(() => {
    setDateValues([]);
  }, [section]);

  useEffect(() => {
    if (!availableYears.length) return;
    if (periodFilter === 'annual' || periodFilter === 'annual_ytd' || periodFilter === 'quarterly' || periodFilter === 'semestral') {
      if (secondaryYear !== 'all' && !availableYears.includes(Number(secondaryYear))) {
        setSecondaryYear('all');
      }
      return;
    }
    if (periodFilter === 'monthly' && !availableYears.includes(Number(secondaryYear))) {
      setSecondaryYear(String(availableYears[availableYears.length - 1]));
    }
  }, [availableYears, periodFilter, secondaryYear]);

  const handlePeriodFilterChange = (nextPeriod) => {
    setPeriodFilter(nextPeriod);
    const now = new Date();
    const latestYear = availableYears[availableYears.length - 1] || now.getFullYear();
    if (nextPeriod === 'annual' || nextPeriod === 'annual_ytd' || nextPeriod === 'quarterly' || nextPeriod === 'semestral') {
      setSecondaryYear('all');
      return;
    }
    if (nextPeriod === 'monthly') {
      setSecondaryYear(
        String(availableYears.includes(now.getFullYear()) ? now.getFullYear() : latestYear)
      );
      setSecondaryMonth(String(now.getMonth()));
    }
  };

  const periodDateFrom = formatApiDate(selectedPeriod?.range?.start);
  const periodDateTo = formatApiDate(selectedPeriod?.range?.end);

  const renderContent = () => {
    if (section === 'asientos') {
      return (
        <AccountingJournalPanel
          key={`asientos-${booksRevision}`}
          ui={ui}
          mode="asientos"
          isDarkMode={isDarkMode}
          onBooksChanged={notifyBooksChanged}
          dateFrom={periodDateFrom}
          dateTo={periodDateTo}
          selectedPeriod={selectedPeriod}
        />
      );
    }
    if (section === 'diario') {
      return (
        <AccountingJournalPanel
          key={`diario-${booksRevision}`}
          ui={ui}
          mode="diario"
          isDarkMode={isDarkMode}
          onBooksChanged={notifyBooksChanged}
          dateFrom={periodDateFrom}
          dateTo={periodDateTo}
          selectedPeriod={selectedPeriod}
        />
      );
    }
    if (section === 'mayor') {
      return (
        <AccountingLedgerPanel
          key={`mayor-${booksRevision}`}
          focusedAccountIds={mayorFocusedAccountIds}
          dateFrom={periodDateFrom}
          dateTo={periodDateTo}
          selectedPeriod={selectedPeriod}
        />
      );
    }
    if (section === 'rendiciones') {
      return (
        <AccountingRendicionesPanel
          key={`rendiciones-${booksRevision}`}
          ui={ui}
          isDarkMode={isDarkMode}
          onBooksChanged={notifyBooksChanged}
          dateFrom={periodDateFrom}
          dateTo={periodDateTo}
          selectedPeriod={selectedPeriod}
        />
      );
    }
    const meta = REGISTER_META[section];
    return (
      <AccountingRegisterPanel
        key={`register-${section}`}
        ui={ui}
        registerType={section}
        title={meta.title}
        subtitle={meta.subtitle}
        isDarkMode={isDarkMode}
        onBooksChanged={notifyBooksChanged}
        dateFrom={periodDateFrom}
        dateTo={periodDateTo}
        selectedPeriod={selectedPeriod}
        documents={usesSharedRegisterDocs ? sharedRegisterDocs : null}
        documentsLoading={usesSharedRegisterDocs ? sharedRegisterLoading : false}
        documentsError={usesSharedRegisterDocs ? sharedRegisterError : ''}
      />
    );
  };

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          alignItems: 'start',
          columnGap: '16px',
          rowGap: '12px',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <Heading level={2} isDarkMode={isDarkMode}>
            Contabilidad
          </Heading>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: ui.muted }}>
            Libros tributarios/operativos, asientos, diario y mayor.
          </p>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'flex-end',
            gap: '10px',
            flexWrap: 'nowrap',
            marginTop: '22px',
            justifySelf: 'end',
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
            <span style={periodLabelStyle(ui)}>Período</span>
            <select
              value={periodFilter}
              onChange={(event) => handlePeriodFilterChange(event.target.value)}
              style={{ ...periodSelectStyle(ui), minWidth: '180px' }}
            >
              {PERIOD_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          {periodFilter === 'annual' || periodFilter === 'annual_ytd' || periodFilter === 'quarterly' || periodFilter === 'semestral' ? (
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
              <span style={periodLabelStyle(ui)}>Año</span>
              <select
                value={secondaryYear}
                onChange={(event) => setSecondaryYear(event.target.value)}
                style={{ ...periodSelectStyle(ui), minWidth: '110px' }}
              >
                <option value="all">Todas</option>
                {availableYears.map((year) => (
                  <option key={year} value={String(year)}>
                    {year}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {periodFilter === 'monthly' ? (
            <>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
                <span style={periodLabelStyle(ui)}>Año</span>
                <select
                  value={String(
                    secondaryYear === 'all'
                      ? availableYears[availableYears.length - 1] || new Date().getFullYear()
                      : secondaryYear
                  )}
                  onChange={(event) => setSecondaryYear(event.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: '100px' }}
                >
                  {availableYears.map((year) => (
                    <option key={year} value={String(year)}>
                      {year}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
                <span style={periodLabelStyle(ui)}>Mes</span>
                <select
                  value={secondaryMonth}
                  onChange={(event) => setSecondaryMonth(event.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: '100px' }}
                >
                  {MONTH_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </>
          ) : null}
        </div>
      </div>

      <PillTabBar
        ariaLabel="Secciones de contabilidad"
        items={NAV_ITEMS}
        activeKey={section}
        onChange={(key) => {
          setSection(key);
          if (key !== 'mayor') setMayorFocusedAccountIds([]);
        }}
        style={{ marginBottom: 16 }}
      />

      <div style={{ display: 'grid', gap: 12, minWidth: 0 }}>
        {section === 'ventas' ? (
          <AccountingSalesOverview
            documents={sharedRegisterDocs}
            selectedPeriod={selectedPeriod}
            chartColumns={chartColumns}
            periodFilter={periodFilter}
            periodTitle={
              PERIOD_OPTIONS.find((option) => option.value === periodFilter)?.label || selectedPeriod?.title || ''
            }
            isDarkMode={isDarkMode}
            ui={ui}
            loading={sharedRegisterLoading}
          >
            <AccountingMonthlyChart
              section={section}
              refreshKey={booksRevision}
              periods={chartColumns}
              periodRange={selectedPeriod.range}
              selectedPeriod={selectedPeriod}
              onDateValuesChange={handleDateValuesChange}
              documents={sharedRegisterDocs}
              documentsLoading={sharedRegisterLoading}
              documentsError={sharedRegisterError}
              fillWidth
              defaultStackKey="status"
              defaultMetricKey="amount"
            />
          </AccountingSalesOverview>
        ) : (
          <AccountingMonthlyChart
            section={section}
            refreshKey={booksRevision}
            periods={selectedPeriod.columns}
            periodRange={selectedPeriod.range}
            selectedPeriod={selectedPeriod}
            onDateValuesChange={handleDateValuesChange}
            onFocusedAccountIdsChange={
              section === 'mayor' ? handleMayorAccountFocus : undefined
            }
            documents={usesSharedRegisterDocs ? sharedRegisterDocs : null}
            documentsLoading={usesSharedRegisterDocs ? sharedRegisterLoading : false}
            documentsError={usesSharedRegisterDocs ? sharedRegisterError : ''}
          />
        )}
        <AccountingPeriodSummary
          section={section}
          refreshKey={booksRevision}
          selectedPeriod={selectedPeriod}
          periodTitle={
            selectedPeriod?.title ||
            (periodFilter === 'all' ? `${titles.monthly} · todos los períodos` : titles.monthly)
          }
          documents={usesSharedRegisterDocs ? sharedRegisterDocs : null}
          documentsLoading={usesSharedRegisterDocs ? sharedRegisterLoading : false}
          documentsError={usesSharedRegisterDocs ? sharedRegisterError : ''}
        />
        {renderContent()}
      </div>
    </div>
  );
};

export default FinanceAccountingWorkspace;
